import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Add, Delete, Send, Visibility } from '@mui/icons-material';

import Container from '../../components/blocks/container';
import InfoBox from '../../components/blocks/messages/info-box';
import CustomModal from '../../components/blocks/custom-modal';
import CommonButton from '../../components/buttons/common-button';

import PartOrdersService from '../../services/PartOrdersService';

/**
 * PartsRequestsPage Component
 * Displays parts requests grouped by folder and intervention date.
 */
const PartsRequestsPage = () => {
    const navigate = useNavigate();

    /** State to hold all part requests */
    const [partsRequests, setPartsRequests] = useState([]);
    /** State to track selected rows */
    const [selectedRows, setSelectedRows] = useState({});
    /** State to mark invalid rows for validation */
    const [invalidRows, setInvalidRows] = useState([]);
    /** State for displaying error messages */
    const [errorMessage, setErrorMessage] = useState(null);
    /** State for displaying success messages */
    const [successMessage, setSuccessMessage] = useState(null);

    /** State for delete confirmation modal open/close */
    const [modalOpen, setModalOpen] = useState(false);
    /** State to track index pending deletion */
    const [pendingDeleteIndex, setPendingDeleteIndex] = useState(null);

    /** State for confirmation modal */
    const [confirmModalOpen, setConfirmModalOpen] = useState({ open: false, type: null });

    /**
     * Fetches pending part orders from API and updates state
     */
    const fetchData = async () => {
        try {
            const data = await PartOrdersService.getPartOrdersPending();
            setPartsRequests(data);
        } catch (err) {
            console.error('Error fetching data:', err);
            setErrorMessage('Impossible de charger les demandes de pièces.');
        }
    };

    // Fetch data on component mount
    useEffect(() => {
        fetchData();
    }, []);

    /**
     * Handles input changes for a specific row and field
     */
    const handleChange = (index, field, value) => {
        setErrorMessage(null);
        setSuccessMessage(null);
        setPartsRequests(prev => {
            const updated = [...prev];
            updated[index] = {
                ...updated[index],
                [field]: field === 'quantity' ? parseInt(value, 10) : value,
            };
            return updated;
        });
        setInvalidRows(prev => prev.filter(i => i !== index));
    };

    /**
     * Confirms deletion of a row, calling API if needed and updating state
     */
    const confirmDelete = async (index) => {
        const row = partsRequests[index];
        if (row.id) {
            try {
                await PartOrdersService.deletePartOrder(row.id);
            } catch (err) {
                console.error('API deletion error:', err);
                setErrorMessage('Erreur lors de la suppression de la pièce.');
                setModalOpen(false);
                return;
            }
        }
        setPartsRequests(prev => prev.filter((_, i) => i !== index));
        setSelectedRows(prev => {
            const copy = { ...prev };
            delete copy[index];
            return copy;
        });
        setInvalidRows(prev => prev.filter(i => i !== index));
        setModalOpen(false);
    };

    /**
     * Handles delete action; shows confirmation if it's the last row for an appointment
     */
    const handleDelete = (index) => {
        const row = partsRequests[index];
        const countForAppointment = partsRequests.filter(
            r => r.id_appointment === row.id_appointment
        ).length;

        if (countForAppointment === 1) {
            setPendingDeleteIndex(index);
            setModalOpen(true);
        } else {
            confirmDelete(index);
        }
    };

    /**
     * Adds a new empty row under given folder and intervention
     */
    const handleAddRow = (folder, interventionId, technicianId, technician, appointmentDate) => {
        const newRow = {
            id: null,
            folder_ref: folder,
            part_name: '',
            part_reference: '',
            quantity: 1,
            id_appointment: interventionId,
            id_technician: technicianId,
            technician,
            appointment_date: appointmentDate,
            state: 'EN_ATTENTE',
        };
        setPartsRequests(prev => [...prev, newRow]);
    };

    /**
     * Toggles selection of a single row
     */
    const toggleRow = index => {
        setSelectedRows(prev => ({
            ...prev,
            [index]: !prev[index],
        }));
    };

    /**
     * Toggles selection for all rows in a group
     */
    const toggleGroup = rows => {
        const allSelected = rows.every(r => selectedRows[getGlobalIndex(r)]);
        const updated = { ...selectedRows };
        rows.forEach(r => {
            const idx = getGlobalIndex(r);
            updated[idx] = !allSelected;
        });
        setSelectedRows(updated);
    };

    /**
     * Validates rows (all or selected) and returns indices of invalid ones
     */
    const validateRows = onlySelected => {
        const invalid = [];
        partsRequests.forEach((row, index) => {
            if (onlySelected && !selectedRows[index]) return;

            const hasName = row.part_name?.trim().length > 0;
            const hasRef = row.part_reference?.trim().length > 0;
            const validQty = typeof row.quantity === 'number' && row.quantity >= 1;

            if (!hasName || !hasRef || !validQty) invalid.push(index);
        });
        setInvalidRows(invalid);
        return invalid;
    };

    /**
     * Sends all requests after validation, updates state and refreshes table
     */
    const sendAll = async () => {
        if (validateRows(false).length > 0) {
            setErrorMessage('Veuillez corriger les erreurs dans les lignes invalides. Les champs Nom, Référence et Quantité sont requis.');
            return;
        }

        try {
            const promises = partsRequests.map(request => {
                const base = {
                    part_name: request.part_name,
                    part_reference: request.part_reference,
                    quantity: request.quantity,
                    state: 'TRANSMIS',
                };

                return request.id
                    ? PartOrdersService.editPartOrder(request.id, base)
                    : PartOrdersService.createPartOrder({
                        ...base,
                        id_appointment: request.id_appointment,
                    });
            });
            await Promise.all(promises);

            await fetchData();
            setSelectedRows({});
            setInvalidRows([]);
            setSuccessMessage('Vos demandes de pièces ont été envoyées avec succès.');
        } catch (err) {
            console.error('Error sending all:', err);
            setErrorMessage('Erreur lors de l\'envoi de la commande de pièces.');
        }
    };

    /**
     * Sends only selected requests after validation and refreshes table
     */
    const sendSelected = async () => {
        if (validateRows(true).length > 0) {
            setErrorMessage('Veuillez corriger les erreurs dans les lignes sélectionnées. Les champs Nom, Référence et Quantité sont requis.');
            return;
        }

        try {
            const promises = partsRequests
                .map((r, i) => {
                    if (!selectedRows[i]) return null;

                    const base = {
                        part_name: r.part_name,
                        part_reference: r.part_reference,
                        quantity: r.quantity,
                        state: 'TRANSMIS',
                    };

                    return r.id
                        ? PartOrdersService.editPartOrder(r.id, base)
                        : PartOrdersService.createPartOrder({
                            ...base,
                            id_appointment: r.id_appointment,
                        });
                })
                .filter(Boolean);

            await Promise.all(promises);

            await fetchData();
            setSelectedRows({});
            setInvalidRows([]);
            setSuccessMessage('Vos demandes de pièces sélectionnées ont été envoyées avec succès.');
        } catch (err) {
            console.error('Error sending selected:', err);
            setErrorMessage('Erreur lors de l\'envoi des demandes sélectionnées.');
        }
    };

    /**
     * Handles confirmation modal for delete action
     */
    const handleConfirmModal = (type) => {
        if (type === 'all') {
            sendAll();
        } else if (type === 'selected') {
            sendSelected();
        }
        setConfirmModalOpen({ open: false, type: null });
    };

    /**
     * Returns the global index of a given row
     */
    const getGlobalIndex = row => partsRequests.indexOf(row);

    /**
     * Groups requests by folder and appointment date
     */
    const grouped = partsRequests.reduce((acc, req) => {
        const folder = req.folder_ref || 'Sans dossier';
        const dateKey = req.appointment_date || 'Sans date';
        acc[folder] = acc[folder] || {};
        acc[folder][dateKey] = acc[folder][dateKey] || { technician: req.technician, rows: [] };
        acc[folder][dateKey].rows.push(req);
        return acc;
    }, {});

    return (
        <Container
            className="parts-requests-page"
            title="Vos demandes de pièces"
            titleBackground="var(--primary-color)"
        >
            {(errorMessage || successMessage) && (
                <InfoBox
                    text={errorMessage || successMessage}
                    type={errorMessage ? 'error' : 'success'}
                    disabledCloseBtn={errorMessage ? false : true}
                />
            )}

            {partsRequests.length === 0 ? (
                <div className="no-requests-global">
                    Aucune demande de pièces en cours
                </div>
            ) : (
                <React.Fragment>
                    {Object.entries(grouped).map(([folder, dateGroups]) =>
                        Object.entries(dateGroups).map(([appointmentDate, { technician, rows }]) => {
                            const header = `Intervention du ${appointmentDate} - Dossier N° ${folder}`;
                            const groupSelected = rows.every(r => selectedRows[getGlobalIndex(r)]);

                            return (
                                <section key={`${folder}-${appointmentDate}`} className="appointment-parts-order">
                                <h2>
                                    {header}
                                    <CommonButton
                                        label="Consulter le dossier"
                                        autoWidth
                                        onClick={() => navigate(`/folders/${folder}/overview-only`)}
                                        icon={<Visibility />}
                                        className="btn-validate-request"
                                    />
                                </h2>

                                <table className="appointment-parts-order-table w-100">
                                    <thead>
                                        <tr>
                                            <th>
                                                <input
                                                    type="checkbox"
                                                    checked={groupSelected}
                                                    onChange={() => toggleGroup(rows)}
                                                />
                                            </th>
                                            <th>Nom</th>
                                            <th>Référence</th>
                                            <th>Quantité</th>
                                            <th>Action</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {rows.length === 0 ? (
                                            <tr>
                                                <td colSpan={5} className="no-rows-message">
                                                    Aucune demande de pièces en cours
                                                </td>
                                            </tr>
                                        ) : (
                                            rows.map((row, idx) => {
                                                const globalIndex = getGlobalIndex(row);
                                                const isChecked = !!selectedRows[globalIndex];
                                                const isInvalid = invalidRows.includes(globalIndex);

                                                return (
                                                    <tr
                                                        key={`${folder}-${appointmentDate}-${idx}`}
                                                        className={isInvalid ? 'invalid-row' : ''}
                                                    >
                                                        <td data-label="Sélection">
                                                            <input
                                                                type="checkbox"
                                                                checked={isChecked}
                                                                onChange={() => toggleRow(globalIndex)}
                                                            />
                                                        </td>
                                                        <td data-label="Nom de la pièce">
                                                            <input
                                                                type="text"
                                                                value={row.part_name || ''}
                                                                onChange={e => handleChange(globalIndex, 'part_name', e.target.value)}
                                                            />
                                                        </td>
                                                        <td data-label="Référence de la pièce">
                                                            <input
                                                                type="text"
                                                                value={row.part_reference || ''}
                                                                onChange={e => handleChange(globalIndex, 'part_reference', e.target.value)}
                                                            />
                                                        </td>
                                                        <td data-label="Quantité">
                                                            <input
                                                                type="number"
                                                                min="1"
                                                                value={row.quantity || 1}
                                                                onChange={e => handleChange(globalIndex, 'quantity', e.target.value)}
                                                            />
                                                        </td>
                                                        <td>
                                                            <CommonButton
                                                                label="Supprimer"
                                                                autoWidth
                                                                icon={<Delete />}
                                                                onClick={() => handleDelete(globalIndex)}
                                                                className="btn-remove-row"
                                                            />
                                                        </td>
                                                    </tr>
                                                );
                                            })
                                        )}
                                    </tbody>
                                </table>

                                <CommonButton
                                    label="Ajouter une ligne"
                                    icon={<Add />}
                                    onClick={() =>
                                        handleAddRow(
                                            folder,
                                            rows[0]?.id_appointment || null,
                                            rows[0]?.id_technician || null,
                                            technician,
                                            appointmentDate
                                        )
                                    }
                                    className="btn-add-row"
                                />
                                </section>
                            );
                        })
                    )}

                    <div className="actions-bar">
                        <CommonButton
                            label="Envoyer toutes les demandes"
                            icon={<Send />}
                            onClick={() => setConfirmModalOpen({ open: true, type: 'all' })}
                            className="btn-send-all"
                        />
                        <CommonButton
                            label="Envoyer les demandes sélectionnées"
                            icon={<Send />}
                            onClick={() => setConfirmModalOpen({ open: true, type: 'selected' })}
                            className="btn-send-selected"
                        />
                    </div>

                    {modalOpen && (
                        <CustomModal
                            open
                            title="Attention"
                            handleSave={() => confirmDelete(pendingDeleteIndex)}
                            onClose={() => setModalOpen(false)}
                        >
                            <InfoBox
                                text="Cette action est irréversible. Vous êtes sur le point de supprimer la dernière ligne pour cette intervention. La commande de pièces sera annulée et vous ne pourrez plus commander pour cette intervention. Voulez-vous continuer ?"
                                type="warning"
                                disabledCloseBtn
                            />
                        </CustomModal>
                    )}

                    {confirmModalOpen.open && (
                        <CustomModal
                            open
                            title="Confirmation"
                            handleSave={() => handleConfirmModal(confirmModalOpen.type)}
                            onClose={() => setConfirmModalOpen({ open: false, type: null })}
                        >
                            <InfoBox
                                text={`Êtes-vous sûr de vouloir envoyer ${confirmModalOpen.type === 'all' ? 'toutes les demandes' : 'les demandes sélectionnées'} ?`}
                                type="info"
                                disabledCloseBtn
                            />
                        </CustomModal>
                    )}
                </React.Fragment>
            )}
        </Container>
    );
};

export default PartsRequestsPage;
